import { domToBlob } from "modern-screenshot";

import { matchPageScheme } from "./page-scheme";

const PHOTO_TIMEOUT_MS = 20_000;
// The card is laid out at the UI's own size; 3x makes it big enough for messengers.
const CAPTURE_SCALE = 3;

/**
 * Downloads a photo into an object URL the capture can read without touching
 * the network again; revoke it once the capture is done. Resolves to null when
 * the photo can't be fetched or decoded, so a missing photo turns into the
 * card's placeholder instead of failing the report.
 *
 * The cache is bypassed on purpose: the modal's carousel already loaded these
 * photos without CORS, and a cached copy of that response carries no
 * Access-Control-Allow-Origin, which would fail this cross-origin fetch.
 */
export const loadReportPhoto = async (url: string): Promise<string | null> => {
  let objectUrl: string | null = null;
  try {
    const response = await fetch(matchPageScheme(url), {
      mode: "cors",
      cache: "no-store",
      signal: AbortSignal.timeout(PHOTO_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return null;

    objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.src = objectUrl;
    await img.decode();
    return objectUrl;
  } catch {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    return null;
  }
};

/**
 * CSS zoom for the off-screen wrapper the card is captured from. The capture
 * freezes the card's computed sizes and redraws them at zoom 1. Chromium lays
 * the page out at devicePixelRatio (browser zoom, OS scaling, most Android
 * screens): at a fractional ratio borders snap to device pixels and text
 * measures a hair narrower, so words wrap in the image. Undoing the ratio lays
 * the card out at zoom 1 too. WebKit and Gecko lay out apart from the ratio,
 * and the same zoom would shrink the card's layout there, so they keep zoom 1.
 */
export const captureLayoutZoom = () =>
  // only Chromium exposes userAgentData
  "userAgentData" in navigator ? 1 / window.devicePixelRatio : 1;

/** Captures the rendered report card as a PNG at CAPTURE_SCALE. */
export const captureReportImage = async (node: HTMLElement): Promise<Blob> => {
  await document.fonts.ready;
  await Promise.all(
    Array.from(node.querySelectorAll("img")).map((img) => img.decode().catch(() => undefined)),
  );

  return domToBlob(node, {
    // The card's wrapper may be zoomed (captureLayoutZoom), and the library would
    // take the zoomed size from getBoundingClientRect; offset sizes ignore zoom.
    width: node.offsetWidth,
    height: node.offsetHeight,
    scale: CAPTURE_SCALE,
    type: "image/png",
    backgroundColor: "#ffffff",
  });
};
